function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useEffect, useId } from 'react';
import { DragDropProvider, useDraggable, useDroppable } from '@dnd-kit/react';
import { closestCenter, defaultCollisionDetection } from '@dnd-kit/collision';
import { CollisionPriority } from '@dnd-kit/abstract';
import styles from './index.module.styl';
import { handleListReorder } from './utils';
import { DraggableEntryProps, DragMetadata, EntryGenerator, EntrySetter } from './types';

/**
 * A drop target for sorting draggable entries. This is used to create a drop zone above and below the entry.
 */
export function DraggableEntrySortDropTarget<T extends DragMetadata>(props: DraggableEntryProps<T>) {
  const dragId = useId();
  const {
    ref,
    isDropTarget: isDropping
  } = useDroppable<DragMetadata>({
    id: dragId,
    collisionDetector: closestCenter,
    collisionPriority: CollisionPriority.Normal,
    type: props.data.type,
    data: props.data,
    accept: source => {
      // ensures entries within the same group and type can be sorted
      return source.data.group === props.data.group;
    }
  });
  return <div className={styles.dropTarget} ref={ref} data-drop-zone data-sentry-component="DraggableEntrySortDropTarget" data-sentry-source-file="index.tsx">
      {isDropping && <div className={styles.indicator} />}
    </div>;
}

/**
 * Provides a generic interface for making components draggable.
 *
 * Passes in the following refs to the child component:
 *
 * - `ref` specifies the draggable area of the component
 * - `dropRef` specifies the droppable area of the component when receiving a drop operation
 * - `handleRef` specifies where the component can be dragged from
 */
export function DraggableEntry<T extends DragMetadata>(props: DraggableEntryProps<T>) {
  const element = React.Children.only(props.children);
  const data = props.data;
  const id = useId();
  const ref = React.useRef<HTMLDivElement>(null);
  const dragId = "".concat(id, "-").concat(data.index);
  const {
    ref: draggableRef,
    handleRef
  } = useDraggable({
    id: "".concat(dragId, "-draggable"),
    feedback: "clone",
    type: data.type,
    disabled: props.disabled,
    data
  });

  /**
   * This is the droppable zone target for the entry itself
   * it allows dropping other items onto the entry with
   */
  const {
    ref: droppableRef,
    isDropTarget
  } = useDroppable({
    collisionDetector: defaultCollisionDetection,
    collisionPriority: CollisionPriority.Normal,
    id: "".concat(dragId, "-droppable"),
    type: data.type,
    accept(source) {
      var _fn;
      if (!source.type || typeof source.type !== "string" || !props.accepts) {
        return false;
      }
      const fn = props.accepts[source.type as T[`type`]];
      if (!fn) {
        return false;
      }
      return (_fn = fn(source.data as T)) !== null && _fn !== void 0 ? _fn : false;
    },
    data
  });
  if (!props.children || !React.isValidElement(props.children)) {
    throw new Error("DraggableEntry must have exactly one child");
  }
  useEffect(() => {
    draggableRef(ref.current);
    if (!props.useDropRef) {
      droppableRef(ref.current);
    }
  }, [ref.current]);
  useEffect(() => {
    var _ref$current;
    (_ref$current = ref.current) === null || _ref$current === void 0 || _ref$current.toggleAttribute("data-dnd-is-drop-target", isDropTarget);
  }, [isDropTarget]);
  return <>
      {!props.disableSortZones && <DraggableEntrySortDropTarget {...props} />}

      {React.cloneElement(element, {
      ref,
      handleRef: !props.disabled ? handleRef : undefined,
      dropRef: props.useDropRef ? droppableRef : undefined
    })}

      {/* droppable target for bottom of the list  */}
      {props.isLast && <DraggableEntrySortDropTarget {...props} data={_objectSpread(_objectSpread({}, data), {}, {
      index: data.index + 1
    })} />}
    </>;
}

/**
 * Provides a basic implementation of a draggable list with arbitrary content.
 *
 * Each item in the list will receive a `handleRef` prop that can be used to attach a drag handle and
 * must export a `ref` prop to be used as the draggable element.
 */
export function DraggableEntriesList(props: {
  entries: Obj[];
  children: Array<React.ReactElement<typeof DraggableEntry>>;
  setEntries: EntrySetter;
  entryGenerator?: EntryGenerator;
}) {
  return <DragDropProvider onDragEnd={event => {
    handleListReorder({
      event,
      entries: props.entries,
      entryGenerator: props.entryGenerator,
      setEntries: props.setEntries
    });
  }} data-sentry-element="DragDropProvider" data-sentry-component="DraggableEntriesList" data-sentry-source-file="index.tsx">
      {/* renders all sortable items */}
      {props.children}
    </DragDropProvider>;
}